/**
 * Application role constants and helpers — mirrors platform_core/auth/rbac.py.
 *
 * Role hierarchy (lowest → highest):
 *   user  <  admin  <  superadmin
 *
 * Usage:
 *   hasMinRole(profile.roles, AppRole.ADMIN)   // true for admin + superadmin
 */

export const AppRole = {
  USER: 'user',
  ADMIN: 'admin',
  SUPERADMIN: 'superadmin',
} as const;

export type AppRoleType = (typeof AppRole)[keyof typeof AppRole];

const ROLE_LEVELS: Record<string, number> = {
  [AppRole.USER]: 1,
  [AppRole.ADMIN]: 2,
  [AppRole.SUPERADMIN]: 3,
};

/**
 * Returns true if *userRoles* includes *required* or a higher role.
 * An empty roles array always returns false.
 */
export function hasMinRole(userRoles: string[], required: string): boolean {
  const requiredLevel = ROLE_LEVELS[required] ?? 0;
  const userLevel = Math.max(0, ...userRoles.map((r) => ROLE_LEVELS[r] ?? 0));
  return userLevel >= requiredLevel;
}

/** Returns the highest role name held by the user, or undefined. */
export function effectiveRole(userRoles: string[]): AppRoleType | undefined {
  let best: [number, AppRoleType | undefined] = [0, undefined];
  for (const r of userRoles) {
    const lvl = ROLE_LEVELS[r] ?? 0;
    if (lvl > best[0]) best = [lvl, r as AppRoleType];
  }
  return best[1];
}
